/**
 * Builds safe ServiceNow table filters from backend-owned field definitions.
 *
 * The UI never accepts raw encoded queries. Each row stays inside the selected
 * profile's field, operator, and choice vocabulary so Vault administrators can
 * narrow imports without turning DocReview into a general ServiceNow proxy.
 */
import { Plus, X } from 'lucide-react'

import {
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '@/components/ui'
import type {
  ServiceNowFilter,
  ServiceNowFilterField,
  ServiceNowFilterOperator,
  ServiceNowSelectionPreview,
  ServiceNowTableDefinition,
} from '../types'

const operatorLabels: Record<ServiceNowFilterOperator, string> = {
  is: 'is',
  'is-not': 'is not',
  contains: 'contains',
  'starts-with': 'starts with',
  'on-or-after': 'on or after',
  'on-or-before': 'on or before',
}

export function ServiceNowFilters({
  definition,
  filters,
  preview,
  canEdit,
  onChange,
}: {
  definition: ServiceNowTableDefinition
  filters: ServiceNowFilter[]
  preview: ServiceNowSelectionPreview | null
  canEdit: boolean
  onChange: (filters: ServiceNowFilter[]) => void
}) {
  const addFilter = () => {
    const field = definition.filterFields[0]
    if (!field || filters.length >= 8) return
    onChange([...filters, newServiceNowFilter(field)])
  }

  const updateFilter = (index: number, filter: ServiceNowFilter) => {
    onChange(filters.map((current, position) => position === index ? filter : current))
  }

  return (
    <div className="space-y-2.5 rounded-[9px] border border-slate-200 bg-slate-50/60 p-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <Label className="text-xs font-medium text-slate-700">Record filters</Label>
          <p className="mt-0.5 text-xs leading-4 text-slate-500">
            All filters must match · approved fields and operators only
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 shrink-0 text-xs"
          disabled={!canEdit || filters.length >= 8}
          onClick={addFilter}
        >
          <Plus className="mr-1 h-3.5 w-3.5" /> Add filter
        </Button>
      </div>

      {filters.length === 0 && (
        <p className="rounded-md border border-dashed border-slate-200 bg-white px-2.5 py-2 text-xs text-slate-500">
          No filters — every record in the window is imported.
        </p>
      )}

      {filters.map((filter, index) => {
        const definedField = definition.filterFields.find(
          item => item.name === filter.field,
        )
          ?? definition.filterFields[0]
        if (!definedField) return null
        const field = withPreviewChoices(definedField, preview)
        return (
          <div
            key={`${index}-${filter.field}`}
            className="grid items-center gap-2 sm:grid-cols-[52px_minmax(0,1fr)_132px_minmax(0,1fr)_32px]"
          >
            <span className="text-xs text-slate-400">
              {index === 0 ? 'Where' : 'and'}
            </span>
            <Select
              value={filter.field}
              disabled={!canEdit}
              onValueChange={name => {
                const selected = definition.filterFields.find(item => item.name === name)
                if (selected) updateFilter(index, newServiceNowFilter(selected))
              }}
            >
              <SelectTrigger aria-label={`Filter ${index + 1} field`} className="h-9 bg-white text-xs">
                {field.label}
              </SelectTrigger>
              <SelectContent position="popper" align="start">
                {definition.filterFields.map(item => (
                  <SelectItem key={item.name} value={item.name}>{item.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={filter.operator}
              disabled={!canEdit}
              onValueChange={operator => updateFilter(index, {
                ...filter,
                operator: operator as ServiceNowFilterOperator,
              })}
            >
              <SelectTrigger aria-label={`Filter ${index + 1} operator`} className="h-9 bg-white text-xs">
                {operatorLabels[filter.operator]}
              </SelectTrigger>
              <SelectContent position="popper" align="start">
                {field.operators.map(operator => (
                  <SelectItem key={operator} value={operator}>{operatorLabels[operator]}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <FilterValue
              index={index}
              field={field}
              value={filter.value}
              disabled={!canEdit}
              onChange={value => updateFilter(index, { ...filter, value })}
            />

            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-9 w-8 text-slate-400 hover:text-destructive"
              aria-label={`Remove filter ${index + 1}`}
              disabled={!canEdit}
              onClick={() => onChange(filters.filter((_, position) => position !== index))}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        )
      })}
    </div>
  )
}

function FilterValue({
  index,
  field,
  value,
  disabled,
  onChange,
}: {
  index: number
  field: ServiceNowFilterField
  value: string
  disabled: boolean
  onChange: (value: string) => void
}) {
  if (field.valueType === 'choice' || field.valueType === 'boolean') {
    return (
      <Select value={value} disabled={disabled} onValueChange={onChange}>
        <SelectTrigger aria-label={`Filter ${index + 1} value`} className="h-9 bg-white text-xs">
          {field.choices.find(choice => choice.value === value)?.label ?? 'Select value'}
        </SelectTrigger>
        <SelectContent position="popper" align="start">
          {field.choices.map(choice => (
            <SelectItem key={choice.value} value={choice.value}>{choice.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    )
  }
  return (
    <Input
      type={field.valueType === 'date' ? 'date' : 'text'}
      aria-label={`Filter ${index + 1} value`}
      value={value}
      disabled={disabled}
      maxLength={200}
      className="h-9 bg-white text-xs"
      onChange={event => onChange(event.target.value)}
    />
  )
}

export function newServiceNowFilter(
  field: ServiceNowFilterField,
): ServiceNowFilter {
  return {
    field: field.name,
    operator: field.operators[0],
    value: field.choices[0]?.value ?? '',
  }
}

export function setServiceNowFilterValue(
  definition: ServiceNowTableDefinition,
  filters: ServiceNowFilter[],
  fieldName: string,
  value: string,
): ServiceNowFilter[] {
  const field = definition.filterFields.find(item => item.name === fieldName)
  if (!field) return filters
  const operator: ServiceNowFilterOperator = field.operators.includes('is')
    ? 'is'
    : (field.operators[0] ?? 'is')
  const existing = filters.findIndex(filter => filter.field === fieldName)
  const selected = { field: fieldName, operator, value }
  if (existing < 0) return filters.length < 8 ? [...filters, selected] : filters
  return filters.map((filter, index) => index === existing ? selected : filter)
}

function withPreviewChoices(
  field: ServiceNowFilterField,
  preview: ServiceNowSelectionPreview | null,
): ServiceNowFilterField {
  if (field.valueType !== 'choice' || !preview) return field
  const column = preview.columns.find(item => item.filterField === field.name)
  if (!column) return field
  const choices = new Map(field.choices.map(choice => [choice.value, choice.label]))
  for (const row of preview.rows) {
    const value = row.filterValues[field.name]
    const label = row.values[column.field]
    if (value && label) choices.set(value, label)
  }
  return {
    ...field,
    choices: [...choices].map(([value, label]) => ({ value, label })),
  }
}
